import React, { useEffect, useRef, useState } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, Grid, TextField, CircularProgress, Divider, Snackbar, Alert,
    IconButton, Tooltip, Card, CardMedia
} from "@mui/material";
import {
    NavigateNext, CloudUpload, Delete, Save, ArrowBack,
    Campaign, Image as ImageIcon, Close
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Header from "../../component/header/Header";

// Firebase
import { initializeApp } from "firebase/app";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const firebaseConfig = {
    apiKey: "AIzaSyBgoyz9VCXmUTVZLG32UCrSOiegRejv7sU",
    authDomain: "genius-milenia-education.firebaseapp.com",
    projectId: "genius-milenia-education",
    storageBucket: "genius-milenia-education.appspot.com",
    messagingSenderId: "103259384991",
    appId: "1:103259384991:web:9a66b97321936f7156c6aa",
};

const firebaseApp = initializeApp(firebaseConfig);
const storage = getStorage(firebaseApp);

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    filterBg: "#FFF9E6",
    textMain: '#5D4037',
};

const AddAnnouncementScreen = () => {
    const navigation = useNavigate();
    const formikRef = useRef();

    const [file, setFile] = useState(null);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        localStorage.setItem("current_tab", "Announcement");
    }, []);

    const handleFileChange = (event) => {
        if (event.target.files[0]) {
            setFile(event.target.files[0]);
        }
    };

    const handleUpload = async (values) => {
        if (!file) {
            setSnackbar({ open: true, message: "Please upload an announcement image", severity: "warning" });
            return;
        }

        setLoadingSubmit(true);
        const storageRef = ref(storage, `announcements/${Date.now()}_${file.name}`);

        try {
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            await createAnnouncement(values, url);
        } catch (error) {
            setSnackbar({ open: true, message: "Upload failed. Try again.", severity: "error" });
            setLoadingSubmit(false);
        }
    };

    const createAnnouncement = async (values, url) => {
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/announcement`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    imageUrl: url,
                })
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Announcement published successfully!", severity: "success" });
                formikRef.current?.resetForm();
                setFile(null);
                setTimeout(() => navigation("/Portal/Announcement"), 1500);
            } else {
                throw new Error(resJson.message);
            }
        } catch (error) {
            setSnackbar({ open: true, message: error.message || "Failed to publish", severity: "error" });
        } finally {
            setLoadingSubmit(false);
        }
    };

    const initialValues = {
        title: "",
        description: "",
        createdDate: moment().format("HH:mm:ss DD-MM-YYYY"),
        status: "A"
    };

    const validationSchema = yup.object({
        title: yup.string().required("Headline is required"),
        description: yup.string().required("Content body is required"),
    });

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                {/* Navigation */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                            <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Announcement")} sx={{ cursor: 'pointer' }}>Announcements</Link>
                            <Typography color="text.primary">Create</Typography>
                        </Breadcrumbs>
                        <Header title="New Announcement" subtitle="Broadcast a new update to mobile app" />
                    </Box>
                    <Button startIcon={<ArrowBack />} onClick={() => navigation(-1)} color="inherit">Cancel</Button>
                </Stack>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleUpload}
                >
                    {({ values, errors, touched, handleBlur, handleChange }) => (
                        <Form>
                            <Grid container spacing={4}>

                                {/* Image Upload Area */}
                                <Grid item xs={12} md={5}>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Cover Image</Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            height: 350,
                                            borderRadius: 4,
                                            border: '2px dashed #DDD',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            bgcolor: '#FBFBFB'
                                        }}
                                    >
                                        {file ? (
                                            <>
                                                <CardMedia
                                                    component="img"
                                                    image={URL.createObjectURL(file)}
                                                    sx={{ height: '100%', width: '100%', objectFit: 'cover' }}
                                                />
                                                <IconButton
                                                    onClick={() => setFile(null)}
                                                    sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'black' } }}
                                                >
                                                    <Close />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <Stack spacing={1} alignItems="center">
                                                <ImageIcon sx={{ fontSize: 60, color: '#CCC' }} />
                                                <Typography color="text.secondary">No image selected</Typography>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    startIcon={<CloudUpload />}
                                                    sx={{ borderRadius: 2, textTransform: 'none', mt: 2 }}
                                                >
                                                    Choose Photo
                                                    <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                                                </Button>
                                            </Stack>
                                        )}
                                    </Paper>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Recommended size: 1200x600px. JPG or PNG only.
                                    </Typography>
                                </Grid>

                                {/* Content Area */}
                                <Grid item xs={12} md={7}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 4 }}>
                                        <Stack spacing={3}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Announcement Title <span style={{ color: 'red' }}>*</span></Typography>
                                                <TextField
                                                    fullWidth
                                                    name="title"
                                                    placeholder="e.g. Mathematic Seminar"
                                                    value={values.title}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.title && !!errors.title}
                                                    helperText={touched.title && errors.title}
                                                    sx={{ bgcolor: 'white' }}
                                                />
                                            </Box>

                                            <Box>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>Content Description <span style={{ color: 'red' }}>*</span></Typography>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={8}
                                                    name="description"
                                                    placeholder="Write the full details of your announcement here..."
                                                    value={values.description}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.description && !!errors.description}
                                                    helperText={touched.description && errors.description}
                                                    sx={{ bgcolor: 'white' }}
                                                />
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    <Divider sx={{ my: 3 }} />

                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            disabled={loadingSubmit}
                                            startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                            sx={{
                                                bgcolor: colors.accent,
                                                color: 'black',
                                                px: 6,
                                                py: 1.5,
                                                borderRadius: 3,
                                                fontWeight: 'bold',
                                                '&:hover': { bgcolor: colors.accentDark }
                                            }}
                                        >
                                            {loadingSubmit ? "Publishing..." : "Publish Announcement"}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Box>
        </Box>
    );
};

export default AddAnnouncementScreen;